import { useState, useRef, useEffect, SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"
import { RiArrowDownSLine, RiCheckLine, RiCloseLine } from "@remixicon/react"

import Selector from "@/components/form/selector/Selector"
import Chip from "@/components/chip/Chip"
import ColorPicker from "@/app/report/components/setting/ColorPicker"
import { CHART_TYPE } from "@/app/report/common/constants"
import { normalizeDecimalPoint } from "@/app/report/common/tools"
import type { OptionItem } from "./types"

// NOTE: RadioGroup, ToggleSwitch, DropdownSelector have been replaced with shared components:
// - RadioGroup → @/components/form/radioGroup/RadioGroup
// - ToggleSwitch → @/components/form/toggle/Toggle
// - DropdownSelector → @/components/form/selector/Selector

// ──────────────────────────────────────────────
// Multi-select dropdown (Session / Player)
// ──────────────────────────────────────────────

interface MultiSelectDropdownProps {
  label: string
  items: Array<{ id: number; name: string; subtitle?: string | null }>
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

export function MultiSelectDropdown({
  label,
  items,
  selectedIds,
  onChange,
}: MultiSelectDropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const allSelected = selectedIds.length === items.length && items.length > 0

  const toggleItem = (id: number) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((i) => i !== id)
      : [...selectedIds, id]
    onChange(next)
  }

  const toggleAll = () => {
    onChange(allSelected ? [] : items.map((i) => i.id))
  }

  if (items.length === 0) return null

  return (
    <div className="PropertyPanel__field" ref={ref}>
      <label>{label}</label>
      <button className="PropertyPanel__select-btn" onClick={() => setOpen((prev) => !prev)}>
        <span className="PropertyPanel__select-btn-text">{label}</span>
        {selectedIds.length > 0 && <Chip type="number" label={selectedIds.length.toString()} />}
        {selectedIds.length > 0 && (
          <span
            className="PropertyPanel__select-btn-clear"
            onClick={(e) => {
              e.stopPropagation()
              onChange([])
            }}>
            <RiCloseLine size={14} />
          </span>
        )}
        <RiArrowDownSLine
          size={16}
          className={`PropertyPanel__select-btn-arrow ${open ? "PropertyPanel__select-btn-arrow--open" : ""}`}
        />
      </button>
      {open && (
        <div className="PropertyPanel__dropdown">
          <div className="PropertyPanel__dropdown-item" onClick={toggleAll}>
            <span
              className={`PropertyPanel__dropdown-check ${allSelected ? "PropertyPanel__dropdown-check--checked" : ""}`}>
              {allSelected && <RiCheckLine size={12} />}
            </span>
            <span className="PropertyPanel__dropdown-item-text">
              {t("selectAll", "Select All")} ({items.length})
            </span>
          </div>
          <div className="PropertyPanel__dropdown-divider" />
          {items.map((item) => {
            const checked = selectedIds.includes(item.id)
            return (
              <div
                key={item.id}
                className="PropertyPanel__dropdown-item"
                onClick={() => toggleItem(item.id)}>
                <span
                  className={`PropertyPanel__dropdown-check ${checked ? "PropertyPanel__dropdown-check--checked" : ""}`}>
                  {checked && <RiCheckLine size={12} />}
                </span>
                <span className="PropertyPanel__dropdown-item-text">
                  {item.name}
                  {item.subtitle && (
                    <span className="PropertyPanel__dropdown-item-sub"> {item.subtitle}</span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────
// Metric Dropdown (chips inside dropdown button)
// ──────────────────────────────────────────────

interface MetricSelectorProps {
  label: string
  selectedMetrics: string[]
  metricOptions: OptionItem[]
  onAdd: (metricKey: string) => void
  onRemove: (metricKey: string) => void
  translateKey: (key: string) => string
  maxCount?: number
}

const VISIBLE_CHIP_COUNT = 4

export function MetricSelector({
  label,
  selectedMetrics,
  metricOptions,
  onAdd,
  onRemove,
  translateKey,
  maxCount,
}: MetricSelectorProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [expanded, setExpanded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus()
    }
  }, [open])

  const searchLower = search.toLowerCase()
  const selectedSet = new Set(selectedMetrics)

  // Filter by search query
  const filteredSelected = search
    ? selectedMetrics.filter((key) => translateKey(key).toLowerCase().includes(searchLower))
    : selectedMetrics
  const filteredAvailable = metricOptions.filter(
    (o) => !selectedSet.has(o.value) && (!search || o.label.toLowerCase().includes(searchLower)),
  )

  const isMaxReached = maxCount != null && selectedMetrics.length >= maxCount

  const visibleChips = selectedMetrics.slice(0, VISIBLE_CHIP_COUNT)
  const overflowCount = selectedMetrics.length - VISIBLE_CHIP_COUNT

  const handleToggleOpen = () => {
    setOpen((prev) => {
      if (prev) setSearch("")
      return !prev
    })
  }

  return (
    <div className="PropertyPanel__field" ref={ref}>
      <label>{label}</label>
      {/* Select button */}
      <button className="PropertyPanel__select-btn" onClick={handleToggleOpen}>
        <span className="PropertyPanel__select-btn-text">
          {selectedMetrics.length > 0
            ? `${selectedMetrics.length} ${t("selected", "Selected")}`
            : t("selectMetrics", "Select metrics...")}
        </span>
        <RiArrowDownSLine
          size={16}
          className={`PropertyPanel__select-btn-arrow ${open ? "PropertyPanel__select-btn-arrow--open" : ""}`}
        />
      </button>
      {/* Selected chips below select button */}
      {selectedMetrics.length > 0 && (
        <div className="PropertyPanel__metric-chips">
          {(expanded ? selectedMetrics : visibleChips).map((key) => (
            <span key={key} className="PropertyPanel__metric-chip">
              <span className="PropertyPanel__metric-chip-label">{translateKey(key)}</span>
              <span className="PropertyPanel__metric-chip-remove" onClick={() => onRemove(key)}>
                <RiCloseLine size={12} />
              </span>
            </span>
          ))}
          {!expanded && overflowCount > 0 && (
            <span className="PropertyPanel__metric-chip PropertyPanel__metric-chip--overflow">
              +{overflowCount}
            </span>
          )}
          {selectedMetrics.length > VISIBLE_CHIP_COUNT && (
            <button
              className="PropertyPanel__metric-chip PropertyPanel__metric-chip--toggle"
              onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? t("less", "Less") : t("more", "More")}
            </button>
          )}
        </div>
      )}
      {open && (
        <div className="PropertyPanel__dropdown">
          {/* Search input */}
          <div className="PropertyPanel__dropdown-search">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("searchMetrics", "Search...")}
              className="PropertyPanel__dropdown-search-input"
            />
          </div>
          {/* Already selected metrics (with check) */}
          {filteredSelected.map((key) => (
            <div key={key} className="PropertyPanel__dropdown-item" onClick={() => onRemove(key)}>
              <span className="PropertyPanel__dropdown-check PropertyPanel__dropdown-check--checked">
                <RiCheckLine size={12} />
              </span>
              <span className="PropertyPanel__dropdown-item-text">{translateKey(key)}</span>
            </div>
          ))}
          {filteredSelected.length > 0 && filteredAvailable.length > 0 && (
            <div className="PropertyPanel__dropdown-divider" />
          )}
          {/* Available metrics */}
          {filteredAvailable.map((opt) => (
            <div
              key={opt.value}
              className={`PropertyPanel__dropdown-item ${isMaxReached ? "PropertyPanel__dropdown-item--disabled" : ""}`}
              onClick={() => {
                if (!isMaxReached) onAdd(opt.value)
              }}>
              <span className="PropertyPanel__dropdown-check" />
              <span className="PropertyPanel__dropdown-item-text">{opt.label}</span>
            </div>
          ))}
          {filteredSelected.length === 0 && filteredAvailable.length === 0 && (
            <div className="PropertyPanel__dropdown-empty">{t("noResults", "No results")}</div>
          )}
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────
// Single Metric Selector (Comparison)
// ──────────────────────────────────────────────

interface SingleMetricSelectorProps {
  label: string
  value: string | undefined
  metricOptions: OptionItem[]
  onChange: (metricKey: string) => void
}

export function SingleMetricSelector({
  label,
  value,
  metricOptions,
  onChange,
}: SingleMetricSelectorProps) {
  return (
    <div className="PropertyPanel__field">
      <label>{label}</label>
      <Selector<OptionItem>
        value={
          value ? (metricOptions.find((o) => o.value === value) ?? { value, label: value }) : null
        }
        options={metricOptions}
        labelKey="label"
        onChange={(_: SyntheticEvent, option: OptionItem) => {
          if (option) onChange(option.value)
        }}
      />
    </div>
  )
}

interface MetricSeriesRowsProps {
  label: string
  metrics: string[]
  getChartType: (metricKey: string) => string
  getColor: (index: number) => string
  onChartTypeChange: (metricKey: string, chartType: string) => void
  onColorChange: (index: number, color: string) => void
  translateKey: (key: string) => string
  /** 고를 수 있는 종류 — 세로 Chart by Player 는 막대·누적 막대만 (#185). 없으면 전체 */
  allowedTypes?: string[]
  /** 지표별 최대값(수동 스케일) — null 은 자동. 둘 다 있으면 종류 줄 아래에 입력 칸을 둔다 (2026-10-01 결정) */
  getScale?: (metricKey: string) => number | null
  onScaleChange?: (metricKey: string, value: number | null) => void
}

/**
 * 선택된 지표마다 [지표명, 차트 종류(콤보박스), 색] 한 줄 — Chart by Player·Session Details 공용 (#75, 2026-09-28 요청).
 * 종류 목록은 리포트 설정 화면과 같은 CHART_TYPE 전체(bar/stacked/point/line/area).
 */
export function MetricSeriesRows({
  label,
  metrics,
  getChartType,
  getColor,
  onChartTypeChange,
  onColorChange,
  translateKey,
  allowedTypes,
  getScale,
  onScaleChange,
}: MetricSeriesRowsProps) {
  const { t } = useTranslation()
  if (!metrics.length) {
    return null
  }
  const typeOptions = Object.values(CHART_TYPE)
    .filter((chartType) => !allowedTypes || allowedTypes.includes(chartType))
    .map((chartType) => ({
      value: chartType,
      label:
        chartType === CHART_TYPE.STACKED_BAR
          ? t("stackedBar", "Stacked Bar")
          : chartType.charAt(0).toUpperCase() + chartType.slice(1),
    }))

  return (
    <div className="PropertyPanel__field">
      <label>{label}</label>
      {metrics.map((metricKey, index) => (
        <div key={metricKey} className="PropertyPanel__series-row">
          <span className="PropertyPanel__series-name" title={translateKey(metricKey)}>
            {translateKey(metricKey)}
          </span>
          {/* 둘째 줄에 [종류 | 최대값 | 색] — 종류·최대값이 남은 폭을 반씩 (2026-10-01 결정) */}
          <div className="PropertyPanel__series-controls">
            <select
              className="PropertyPanel__series-type"
              value={getChartType(metricKey)}
              onChange={(e) => onChartTypeChange(metricKey, e.target.value)}
              aria-label={t("chartType", "Chart Type")}>
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {onScaleChange && (
              <input
                className="PropertyPanel__series-scale"
                type="number"
                min={0}
                placeholder={t("maxValue", "Max Value")}
                aria-label={t("maxValue", "Max Value")}
                title={t("maxValue", "Max Value")}
                value={getScale?.(metricKey) ?? ""}
                onChange={(e) =>
                  onScaleChange(metricKey, e.target.value === "" ? null : Number(e.target.value))
                }
              />
            )}
            <ColorPicker
              color={getColor(index)}
              setColor={(color) => onColorChange(index, color)}
              hideLabel
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/** 배치 — Auto: 페이지 경계에 걸리면 다음 페이지에서 시작, Manual: 경계와 상관없이 그대로 (2026-10-01 결정) */
export function LayoutModeSelector({
  value,
  onChange,
}: {
  value: "auto" | "manual"
  onChange: (value: "auto" | "manual") => void
}) {
  const { t } = useTranslation()
  const options = [
    { value: "auto", label: t("layoutAuto", "Auto") },
    { value: "manual", label: t("layoutManual", "Manual") },
  ]

  return (
    <div className="PropertyPanel__field">
      <Selector
        slotProps={{ popper: { className: "PropertyPanel__menu" } }}
        label={t("layout", "Layout")}
        options={options}
        value={options.find((option) => option.value === value) || options[0]}
        onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
          if (newVal) {
            onChange(newVal.value as "auto" | "manual")
          }
        }}
        placeholder={t("select")}
      />
    </div>
  )
}

/** 소수점 자릿수 — 1 / 0.1 / 0.01 / 0.001. 기본 0.1, 템플릿 값이 있으면 그 값 (2026-10-01 결정) */
export function DecimalPlacesSelector({
  value,
  onChange,
}: {
  value: number
  onChange: (value: number) => void
}) {
  const { t } = useTranslation()
  const options = [
    { value: "0", label: "1" },
    { value: "1", label: "0.1" },
    { value: "2", label: "0.01" },
    { value: "3", label: "0.001" },
  ]

  return (
    <div className="PropertyPanel__field">
      <label>{t("decimalPlaces", "Decimal Places")}</label>
      <select
        className="PropertyPanel__series-type PropertyPanel__select-full"
        // 템플릿에는 자릿수(1)와 단위(0.1) 두 꼴이 섞여 있다 — 자릿수로 맞춰 고른다 (2026-10-01 보고)
        value={String(normalizeDecimalPoint(value))}
        aria-label={t("decimalPlaces", "Decimal Places")}
        onChange={(e) => onChange(Number(e.target.value))}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

/** 검색형 단일 지표 선택기 — Table 의 Metrics 와 같은 드롭다운(검색 + 목록), 하나만 고른다 (2026-10-02 요청) */
function SingleMetricSearchSelect({
  value,
  metricOptions,
  onChange,
}: {
  value: string
  metricOptions: OptionItem[]
  onChange: (metricKey: string) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)

    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus()
    }
  }, [open])

  const searchLower = search.toLowerCase()
  const filtered = metricOptions.filter(
    (option) => !search || option.label.toLowerCase().includes(searchLower),
  )
  const selectedLabel = metricOptions.find((option) => option.value === value)?.label ?? value

  return (
    <div className="PropertyPanel__axis-metric" ref={ref}>
      <button
        className="PropertyPanel__select-btn"
        onClick={() =>
          setOpen((prev) => {
            if (prev) {
              setSearch("")
            }

            return !prev
          })
        }>
        <span className="PropertyPanel__select-btn-text">
          {selectedLabel || t("selectMetrics", "Select metrics...")}
        </span>
        <RiArrowDownSLine
          size={16}
          className={`PropertyPanel__select-btn-arrow ${open ? "PropertyPanel__select-btn-arrow--open" : ""}`}
        />
      </button>
      {open && (
        <div className="PropertyPanel__dropdown">
          <div className="PropertyPanel__dropdown-search">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("searchMetrics", "Search...")}
              className="PropertyPanel__dropdown-search-input"
            />
          </div>
          {filtered.map((option) => (
            <div
              key={option.value}
              className="PropertyPanel__dropdown-item"
              onClick={() => {
                onChange(option.value)
                setOpen(false)
                setSearch("")
              }}>
              <span
                className={`PropertyPanel__dropdown-check ${option.value === value ? "PropertyPanel__dropdown-check--checked" : ""}`}>
                {option.value === value && <RiCheckLine size={12} />}
              </span>
              <span className="PropertyPanel__dropdown-item-text">{option.label}</span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="PropertyPanel__dropdown-empty">{t("noResults", "No results")}</div>
          )}
        </div>
      )}
    </div>
  )
}

/** 축 한 줄 — [지표 | 최대값]을 가로로 (Series 줄과 같은 모양, 2026-10-01 요청) */
export function AxisMetricRow({
  label,
  value,
  metricOptions,
  onChange,
  scale,
  onScaleChange,
}: {
  label: string
  value: string
  metricOptions: OptionItem[]
  onChange: (metricKey: string) => void
  scale: number | null
  onScaleChange: (value: number | null) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="PropertyPanel__field">
      <label>{label}</label>
      <div className="PropertyPanel__series-controls">
        <SingleMetricSearchSelect value={value} metricOptions={metricOptions} onChange={onChange} />
        <input
          className="PropertyPanel__series-scale"
          type="number"
          min={0}
          placeholder={t("maxValue", "Max Value")}
          aria-label={`${label} ${t("maxValue", "Max Value")}`}
          title={t("maxValue", "Max Value")}
          value={scale ?? ""}
          onChange={(e) => onScaleChange(e.target.value === "" ? null : Number(e.target.value))}
        />
      </div>
    </div>
  )
}

/** 축 최대값 입력 한 줄 — 비우면 자동 (Scatter X·Y, 2026-10-01 결정) */
export function MaxValueInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: number | null
  onChange: (value: number | null) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="PropertyPanel__series-scale-row">
      <span>{label}</span>
      <input
        className="PropertyPanel__series-scale"
        type="number"
        min={0}
        placeholder={t("auto", "Auto")}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    </div>
  )
}

export type SortCategory = {
  value: string
  label: string
  directions: Array<{ value: "asc" | "desc"; label: string }>
}

/**
 * 정렬 — 드롭다운 둘(기준 · 방향)을 가로로. 기준을 바꾸면 방향 목록도 그 기준의 것으로 바뀐다 (2026-10-01 요청).
 * Chart by Player(지표·이름·포지션)·Session Details(기본 순서·지표) 공용.
 */
export function SortSelectors({
  label,
  categories,
  value,
  onChange,
}: {
  label: string
  categories: SortCategory[]
  value: { key: string; order: "asc" | "desc" }
  onChange: (next: { key: string; order: "asc" | "desc" }) => void
}) {
  const { t } = useTranslation()
  const category = categories.find((c) => c.value === value.key) ?? categories[0]
  if (!category) {
    return null
  }
  const direction =
    category.directions.find((d) => d.value === value.order) ?? category.directions[0]

  return (
    <div className="PropertyPanel__field">
      <label>{label}</label>
      {/* Series 행과 같은 작은 네이티브 select 둘을 가로로 — 기준이 바뀌면 방향 목록도 바뀐다 */}
      <div className="PropertyPanel__sort-row">
        <select
          className="PropertyPanel__series-type PropertyPanel__sort-select"
          value={category.value}
          aria-label={label}
          onChange={(e) => {
            const next = categories.find((c) => c.value === e.target.value)
            if (next) {
              onChange({ key: next.value, order: next.directions[0]?.value ?? "asc" })
            }
          }}>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {/* 방향이 하나뿐인 기준(기본 순서)은 둘째 select 를 두지 않는다 */}
        {category.directions.length > 1 && (
          <select
            className="PropertyPanel__series-type PropertyPanel__sort-select"
            value={direction?.value ?? "asc"}
            aria-label={t("sortOrder", "Order")}
            onChange={(e) =>
              onChange({ key: category.value, order: e.target.value as "asc" | "desc" })
            }>
            {category.directions.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  )
}
